import React from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui";

export type CalendarEventType =
  | "info"
  | "warning"
  | "success"
  | "error"
  | "closed"
  | "open";

interface CalendarEvent {
  date: Date;
  type: CalendarEventType;
  label: string;
}

interface AnalyticsCalendarProps {
  title: string;
  description?: string;
  month: number; // 0-11, where 0 is January
  year: number;
  events: CalendarEvent[];
  className?: string;
}

export const AnalyticsCalendar: React.FC<AnalyticsCalendarProps> = ({
  title,
  description,
  month,
  year,
  events,
  className = "",
}) => {
  // Get day names for header
  const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  // Get the first day of the month
  const firstDayOfMonth = new Date(year, month, 1);
  // Get the last day of the month
  const lastDayOfMonth = new Date(year, month + 1, 0);

  // Get the day of the week for the first day (0-6, where 0 is Sunday)
  const startingDayOfWeek = firstDayOfMonth.getDay();
  // Total days in the month
  const daysInMonth = lastDayOfMonth.getDate();

  // Total cells needed in the calendar (previous month days + current month days)
  const totalCells = Math.ceil((startingDayOfWeek + daysInMonth) / 7) * 7;

  // Function to get event type for a specific date
  const getEventForDate = (day: number): CalendarEvent | undefined => {
    const dateToCheck = new Date(year, month, day);
    return events.find(
      (event) =>
        event.date.getDate() === dateToCheck.getDate() &&
        event.date.getMonth() === dateToCheck.getMonth() &&
        event.date.getFullYear() === dateToCheck.getFullYear()
    );
  };

  // Get event type color class
  const getEventTypeColor = (type: CalendarEventType): string => {
    switch (type) {
      case "info":
        return "bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-100";
      case "warning":
        return "bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-100";
      case "success":
        return "bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-100";
      case "error":
        return "bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-100";
      case "closed":
        return "bg-neutral-100 dark:bg-neutral-800 text-neutral-800 dark:text-neutral-200";
      case "open":
        return "bg-primary-100 dark:bg-primary-900/50 text-primary-800 dark:text-primary-100";
      default:
        return "";
    }
  };

  // Get event type icon or indicator
  const getEventTypeIndicator = (type: CalendarEventType): string => {
    switch (type) {
      case "info":
        return "ℹ️";
      case "warning":
        return "⚠️";
      case "success":
        return "✅";
      case "error":
        return "❌";
      case "closed":
        return "🔒";
      case "open":
        return "🔓";
      default:
        return "";
    }
  };

  // Get month name
  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  return (
    <Card className={className}>
      <CardHeader className="pb-2">
        <CardTitle className="text-lg font-medium">{title}</CardTitle>
        {description ? (
          <CardDescription>{description}</CardDescription>
        ) : (
          <CardDescription>
            {monthNames[month]} {year}
          </CardDescription>
        )}
      </CardHeader>
      <CardContent>
        {/* Calendar grid */}
        <div className="w-full">
          {/* Day names header */}
          <div className="grid grid-cols-7 mb-2">
            {dayNames.map((day, index) => (
              <div key={index} className="text-center py-2 text-sm font-medium">
                {day}
              </div>
            ))}
          </div>

          {/* Calendar cells */}
          <div className="grid grid-cols-7 gap-1">
            {Array.from({ length: totalCells }).map((_, index) => {
              const dayNumber = index - startingDayOfWeek + 1;
              const isCurrentMonth = dayNumber > 0 && dayNumber <= daysInMonth;
              const event = isCurrentMonth
                ? getEventForDate(dayNumber)
                : undefined;

              return (
                <div
                  key={index}
                  className={`
                    relative aspect-square p-1 rounded-md border flex flex-col
                    ${
                      isCurrentMonth
                        ? "bg-card"
                        : "bg-muted/30 text-muted-foreground"
                    }
                    ${event ? getEventTypeColor(event.type) : ""}
                  `}
                >
                  {/* Day number */}
                  <div className="text-sm font-medium">
                    {isCurrentMonth ? dayNumber : ""}
                  </div>

                  {/* Event indicator */}
                  {event && (
                    <div className="mt-auto">
                      <div className="flex items-center text-xs">
                        <span className="mr-1">
                          {getEventTypeIndicator(event.type)}
                        </span>
                        <span className="truncate">{event.label}</span>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Legend */}
        <div className="mt-4 flex flex-wrap gap-3">
          {(
            [
              "info",
              "warning",
              "success",
              "error",
              "closed",
              "open",
            ] as CalendarEventType[]
          ).map((type) => (
            <div key={type} className="flex items-center text-xs">
              <div
                className={`w-3 h-3 rounded-sm mr-1 ${getEventTypeColor(type)}`}
              />
              <span className="capitalize">{type}</span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};
